/* Tasarım belgesini siteye uygulayan saf işlevler (packages/shared/design.ts).

   **Dosya sistemine dokunmuyor.** Aynı kod iki yerde çalışıyor: üreticide
   (generate.ts) ve panelin canlı önizlemesinde (editor/). Önizlemenin
   yayındaki çıktının aynısı olması buna bağlı.

   Ezilmiş bir şey yoksa hiçbir şey üretilmiyor: `designCss` ve `i18nScript`
   boş dize, `fontsHref` null. Üretici bu durumda `design.css`'i yazmıyor ve
   sayfalara bağlantı eklemiyor. */

import { DEFAULT_FONTS, FONTS, walk, type Block, type Design, type FontName, type Palette } from '@frawgraphy/shared';
import { blockCss } from './blocks/css.ts';

/** Arşivdeki karelerin herkese açık adresi. Site hâlâ `legacy` olsa da arşiv R2'de. */
export const DESIGN_MEDIA_HOST = 'https://photos.frawgraphy.com';

export function designMediaUrl(photoId: string): string {
  return `${DESIGN_MEDIA_HOST}/browse/${encodeURIComponent(photoId)}.webp`;
}

/** Belgedeki bütün blok listeleri: ana sayfa, ortak alanlar, sayfa bölgeleri. */
export function allBlockLists(doc: Design): Block[][] {
  const lists: Block[][] = [];
  if (doc.home) lists.push(doc.home);
  if (doc.shared) lists.push(doc.shared);
  for (const list of Object.values(doc.pages ?? {})) if (list) lists.push(list);
  return lists;
}

/* ── CSS ────────────────────────────────────────────────── */

const PALETTE_VARS: Record<keyof Palette, string> = {
  bg: '--bg',
  bgElev: '--bg-elev',
  fg: '--fg',
  fgMuted: '--fg-muted',
  accent: '--accent'
};

/** `#c9b28a` → `rgba(201, 178, 138, 0.16)` — style.css'teki `--accent-soft` ile aynı oran. */
function soft(hex: string): string {
  const n = Number.parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, 0.16)`;
}

function paletteRules(p: Palette): string[] {
  const rules: string[] = [];
  for (const [key, cssVar] of Object.entries(PALETTE_VARS) as [keyof Palette, string][]) {
    const value = p[key];
    if (value) rules.push(`${cssVar}: ${value};`);
  }
  if (p.accent) rules.push(`--accent-soft: ${soft(p.accent)};`);
  return rules;
}

function fontStack(name: FontName): string {
  return `"${name}", ${FONTS[name].fallback}`;
}

/**
 * Tasarımın bütün CSS'i: tema değişkenleri (`:root`) ve blok stilleri
 * (`[data-bid]`, blocks/css.ts). style.css'in sınıflarını yeniden yazmıyor,
 * değişkenlerini eziyor — style.css değişince tasarım ondan ayrı düşmüyor.
 */
export function designCss(doc: Design): string {
  const out: string[] = [];
  const t = doc.tokens;

  const root = [...paletteRules(t.dark)];
  if (t.fontDisplay && t.fontDisplay !== DEFAULT_FONTS.display) root.push(`--font-display: ${fontStack(t.fontDisplay)};`);
  if (t.fontBody && t.fontBody !== DEFAULT_FONTS.body) root.push(`--font-body: ${fontStack(t.fontBody)};`);
  if (root.length > 0) out.push(`:root {\n  ${root.join('\n  ')}\n}`);

  const light = paletteRules(t.light);
  if (light.length > 0) out.push(`:root[data-theme="light"] {\n  ${light.join('\n  ')}\n}`);

  if (t.typeScale && t.typeScale !== 1) {
    /* Yazı adımları (`--step-*`) clamp() ve CSS bir değişkeni kendi üstüne
       tanımlayamıyor, yani adımları tek tek çarpmak style.css'i buraya
       kopyalamak olurdu. Adımların taban ve tavanı rem: kök font boyutunu
       çarpmak hepsini orantılı büyütüyor. Ortadaki vw payı ölçeklenmiyor —
       geniş ekranda etki biraz daha az, kabul edilebilir bir sapma. */
    out.push(`html {\n  font-size: ${(100 * t.typeScale).toFixed(1)}%;\n}`);
  }

  const blocks = allBlockLists(doc).map(blockCss).filter(Boolean).join('\n\n');
  if (blocks) out.push(blocks);
  return out.length > 0 ? `/* Panelin Görünüm editörü üretti — elle düzenlemeyin. */\n${out.join('\n\n')}\n` : '';
}

/** Ek Google Fonts adresi — yalnızca varsayılanların dışında bir yazı tipi seçildiyse. */
export function fontsHref(doc: Design): string | null {
  const wanted = new Set<FontName>();
  const add = (f: FontName | undefined) => {
    if (f && f !== DEFAULT_FONTS.display && f !== DEFAULT_FONTS.body) wanted.add(f);
  };
  add(doc.tokens.fontDisplay);
  add(doc.tokens.fontBody);
  for (const list of allBlockLists(doc)) walk(list, (b) => add(b.style?.font));
  if (wanted.size === 0) return null;
  const families = [...wanted]
    .sort()
    .map((f) => `family=${f.replace(/ /g, '+')}:${FONTS[f].axes}`)
    .join('&');
  return `https://fonts.googleapis.com/css2?${families}&display=swap`;
}

/* ── Dil ────────────────────────────────────────────────── */

/**
 * EN karşılıklar — sitenin sözlüğüne eklenen betik
 * (`assets/js/i18n.design.js`). i18n.js'ten sonra, main.js'ten önce
 * yükleniyor; main.js sözlüğü dil değişiminde okuyor.
 */
export function i18nScript(en: Record<string, string>): string {
  if (Object.keys(en).length === 0) return '';
  /* JSON, `</script>` kaçışıyla: dosya ayrı ama aynı metin bir gün satır içi
     basılırsa da güvenli kalsın. */
  const body = JSON.stringify(en, null, 2).replace(/</g, '\\u003c');
  return `/* Panelin Görünüm editörü üretti — elle düzenlemeyin. */\nObject.assign(((window.FRAWGRAPHY_I18N = window.FRAWGRAPHY_I18N || {}).en = window.FRAWGRAPHY_I18N.en || {}), ${body});\n`;
}

/* ── Sayfalara bağlama ──────────────────────────────────── */

/**
 * Tasarım dosyalarını bir sayfaya bağlar: `design.css` style.css'ten,
 * `i18n.design.js` i18n.js'ten, ek yazı tipi Google Fonts bağlantısından
 * hemen sonra. Göreli önek (`../` blog yazılarında) mevcut bağlantıdan
 * okunuyor, yani her sayfa kendi derinliğinde doğru adresi alıyor.
 */
export function linkDesignAssets(page: string, opts: { css: boolean; js: boolean; fonts: string | null }): string {
  let out = page;
  if (opts.css) {
    out = out.replace(/(<link rel="stylesheet" href="((?:\.\.\/|\/)?)assets\/css\/style\.css[^"]*">)/, '$1\n<link rel="stylesheet" href="$2assets/css/design.css">');
  }
  if (opts.fonts) {
    const href = opts.fonts.replace(/&/g, '&amp;');
    out = out.replace(/(<link rel="stylesheet" href="https:\/\/fonts\.googleapis\.com\/css2[^"]*">)/, `$1\n<link rel="stylesheet" href="${href}">`);
  }
  if (opts.js) {
    out = out.replace(/(<script src="((?:\.\.\/|\/)?)assets\/js\/i18n\.js[^"]*" defer><\/script>)/, '$1\n<script src="$2assets/js/i18n.design.js" defer></script>');
  }
  return out;
}
